'use client';

import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import type { ContentType as TContentType } from '@jlg-website/database/types';
import { ContentType } from '@jlg-website/shared/constants';
import type { ContentTypeTestimonialContentInput } from '@jlg-website/shared/schemas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pencil } from 'lucide-react';

const loading = () => <>Loading...</>;

const TestimonialsForm = dynamic(
  () => import('@/components/admin/contents/content-types/testimonials-form'),
  { loading }
);

export function TestimonialsEditDialogForm({
  value,
  onEdit,
}: {
  value: string;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [contentType, setContentType] = useState<TContentType | null>(null);
  const [defaultValues, setDefaultValues] = useState<ContentTypeTestimonialContentInput | null>(null);

  const handleSave = () => {
    setOpen(false);
    onEdit();
  };

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      const [ct, content] = await Promise.all([
        fetch(`/admin/api/content-type?name=${ContentType.Testimonials}`).then((r) => r.json()),
        fetch(`/admin/api/contents/${value}`).then((r) => r.json()),
      ]);
      setContentType(ct);
      setDefaultValues(content);
    };

    fetchData();
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setContentType(null);
          setDefaultValues(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" aria-label="Edit">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      {open && (
        <DialogContent className="lg:min-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Testimonial</DialogTitle>
            {contentType && defaultValues && (
              <TestimonialsForm
                contentType={contentType}
                defaultValues={defaultValues}
                onSave={handleSave}
                customCancelButton={
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                }
              />
            )}
          </DialogHeader>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default TestimonialsEditDialogForm;
